/**
 * The head of every page.
 *
 * Two consumers, one source: the router applies these to the live document on
 * every navigation, and scripts/prerender.mjs writes the same tags into the
 * HTML file it generates per route. Before this port there was one <title> and
 * one description for the whole site, because there was only ever one URL.
 */

import { absoluteUrl } from "./files";
import { SITE_NAME, type PageMeta } from "./pages";

/** The picture link previews show. The band, as on the header of every page. */
export const PREVIEW_IMAGE_PATH = "/images/tubetaenzig-band-gruppenbild.jpg";

/** A <meta> element, described by the attribute that names it. */
export interface MetaTag {
  /** Standard metadata, e.g. description. */
  name?: string;
  /** Open Graph, which uses `property` rather than `name`. */
  property?: string;
  content: string;
}

/**
 * Every meta tag a page carries beyond charset and viewport.
 *
 * Open Graph is here because a band's links are shared in chat groups far more
 * often than they are typed - without these, a shared link is a bare URL.
 *
 * Without a site URL - which only happens in `npm run dev`, since the build
 * refuses to run without one - the two absolute tags are left out rather than
 * guessed. A wrong og:url is worse than a missing one.
 */
export function metaTags(page: PageMeta, siteUrl: string): MetaTag[] {
  const tags: MetaTag[] = [
    { name: "description", content: page.description },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: "de_CH" },
    { property: "og:title", content: page.title },
    { property: "og:description", content: page.description },
    { name: "twitter:card", content: "summary_large_image" },
  ];

  // The catch-all is the only page that is not indexed. Its `path` is a
  // vue-router matcher, not an address, so pasting it into an absolute URL
  // would declare `.../:pfad(.*)*` as this page's canonical location - for
  // every mistyped address on the site. It gets a noindex instead, which is
  // what a soft 404 should say anyway.
  if (!page.indexed) {
    tags.push({ name: "robots", content: "noindex" });
  }

  if (!siteUrl.trim()) {
    return tags;
  }

  tags.push({
    property: "og:image",
    content: absoluteUrl(siteUrl, PREVIEW_IMAGE_PATH),
  });
  tags.push({
    property: "og:image:alt",
    content:
      "Gruppenbild der Schweizer Ländlermusik-Band tubetänzig aus Laufenburg",
  });

  if (page.indexed) {
    tags.push({ property: "og:url", content: absoluteUrl(siteUrl, page.path) });
  }

  return tags;
}

/**
 * The one address a search engine should treat as this page, or null when
 * there is none: without a site URL, and for the catch-all, whose path is a
 * matcher rather than an address.
 */
export function canonicalUrl(page: PageMeta, siteUrl: string): string | null {
  return page.indexed && siteUrl.trim()
    ? absoluteUrl(siteUrl, page.path)
    : null;
}

/**
 * Writes one meta tag, replacing the element that is already there.
 *
 * Replacing rather than appending is what keeps a navigation from leaving a
 * trail of descriptions behind it, and it is also how the tags index.html
 * shipped for the start page get overwritten on the way to another one.
 */
function selectorFor(tag: MetaTag): string {
  return tag.name
    ? `meta[name="${tag.name}"]`
    : `meta[property="${tag.property}"]`;
}

function upsertMeta(head: HTMLHeadElement, tag: MetaTag): void {
  const selector = selectorFor(tag);

  const element =
    head.querySelector<HTMLMetaElement>(selector) ??
    head.appendChild(document.createElement("meta"));

  if (tag.name) {
    element.setAttribute("name", tag.name);
  } else if (tag.property) {
    element.setAttribute("property", tag.property);
  }
  element.setAttribute("content", tag.content);
}

/**
 * Writes a page's title, description, canonical link and Open Graph tags into
 * the document. Called on every navigation.
 */
/**
 * Every selector this module may have written, so a page that does not carry a
 * tag can take the previous page's copy of it back out. Without that, walking
 * from /kontakt to a mistyped address would leave the contact page's canonical
 * link and og:url standing on the 404.
 */
const MANAGED_SELECTORS = [
  'meta[name="robots"]',
  'meta[property="og:url"]',
  'link[rel="canonical"]',
];

export function applyHead(page: PageMeta, siteUrl: string): void {
  document.title = page.title;

  const head = document.head;
  const tags = metaTags(page, siteUrl);

  for (const tag of tags) {
    upsertMeta(head, tag);
  }

  const canonical = canonicalUrl(page, siteUrl);
  if (canonical) {
    const element =
      head.querySelector<HTMLLinkElement>('link[rel="canonical"]') ??
      head.appendChild(document.createElement("link"));
    element.setAttribute("rel", "canonical");
    element.setAttribute("href", canonical);
  }

  const written = new Set(tags.map(selectorFor));
  if (canonical) {
    written.add('link[rel="canonical"]');
  }
  for (const selector of MANAGED_SELECTORS) {
    if (!written.has(selector)) {
      head.querySelector(selector)?.remove();
    }
  }
}
